import React, { useState, useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, TooltipProps } from 'recharts';
import { Currency, ChartDataPoint, TimeRange } from '../types';

interface PriceChartProps {
  data: ChartDataPoint[];
  isLoading: boolean;
  selectedRange: TimeRange;
  onRangeChange: (range: TimeRange) => void;
}

// Custom Tooltip Component
const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data: ChartDataPoint = payload[0].payload;
    return (
      <div className="bg-dark-blue/80 backdrop-blur-sm border border-[#8A74F9] p-4 rounded-lg shadow-lg text-off-white">
        <p className="font-bold text-lg mb-2">{data.date === 'Now' ? 'Current Price' : `Time: ${data.date}`}</p>
        <p className="text-sm text-light-blue mb-2">
          {new Date(data.timestamp).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
        <div className="border-t border-steel-blue my-2"></div>
        <div className="space-y-1">
          <p className="font-semibold text-base">
            <span className="text-green-400">USD Price:</span> ${data.priceUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="font-semibold text-base">
            <span className="text-blue-400">EUR Price:</span> €{data.priceEUR.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
      </div>
    );
  }
  return null;
};


const PriceChart: React.FC<PriceChartProps> = ({ data, isLoading, selectedRange, onRangeChange }) => {
  const [currency, setCurrency] = useState<Currency>(Currency.USD);
  const dataKey = currency === Currency.USD ? 'priceUSD' : 'priceEUR';
  
  const timeRanges: TimeRange[] = ['1D', '1W', '3M', '6M', '1Y'];

  const maxPrice = useMemo(() => {
    if (!data || data.length === 0) return 0;
    const values = data.map(p => p[dataKey as keyof ChartDataPoint] as number);
    return Math.max(...values);
  }, [data, dataKey]);

  const monthlyTicks = useMemo(() => {
    if (selectedRange === '1D' || selectedRange === '1W' || !data || data.length === 0) {
      return undefined; // Use auto-ticks for shorter ranges
    }

    const ticks: number[] = [];
    const seenMonths = new Set<string>();

    for (const point of data) {
      const date = new Date(point.timestamp);
      const monthYearKey = `${date.getFullYear()}-${date.getMonth()}`;
      if (!seenMonths.has(monthYearKey)) {
        seenMonths.add(monthYearKey);
        ticks.push(point.timestamp);
      }
    }
    return ticks;
  }, [data, selectedRange]);

  const xAxisTickFormatter = (timestamp: number): string => {
    const date = new Date(timestamp);
    // Display different formats based on the selected time range
    switch(selectedRange) {
      case '1D':
        // Show hours for the 1-day view
        return date.toLocaleTimeString('en-US', { hour: 'numeric' });
      case '1W':
        // Show month and day for the 1-week view
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      case '3M':
      case '6M':
      case '1Y':
      default:
        // Show just the month for longer views
        return date.toLocaleDateString('en-US', { month: 'short' });
    }
  };
  
  const renderChart = () => {
    if (isLoading) {
      return (
        <div style={{ width: '100%', height: 350 }} className="flex items-center justify-center">
          <p className="text-light-blue">Loading chart data...</p>
        </div>
      );
    }
    return (
      <div style={{ width: '100%', height: 350 }}>
        <ResponsiveContainer>
          <AreaChart
            data={data}
            margin={{ top: 10, right: 0, left: 0, bottom: 20 }}
          >
            <defs>
              <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8A74F9" stopOpacity={0.4}/>
                <stop offset="95%" stopColor="#8A74F9" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <XAxis 
              dataKey="timestamp"
              tickFormatter={xAxisTickFormatter}
              stroke="#778DA9"
              tick={{ fontSize: 12 }}
              ticks={monthlyTicks}
              interval={monthlyTicks ? 0 : 'auto'}
              dy={10}
            />
            <YAxis domain={['dataMin', 'dataMax']} hide={true} />
            <Tooltip
                cursor={{ stroke: '#8A74F9', strokeWidth: 1, strokeDasharray: '3 3' }}
                content={<CustomTooltip />}
            />
            <Area 
                type="monotone" 
                dataKey={dataKey} 
                strokeWidth={2}
                stroke="#A798F8" 
                fill="url(#chartGradient)" 
                dot={false}
            />
             <ReferenceLine y={maxPrice} stroke="#A798F8" strokeDasharray="1 8" strokeWidth={1.5} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-4 mb-4">
        <h2 className="text-2xl font-bold text-off-white">Price History ({selectedRange})</h2>
        <div className="flex items-center gap-2">
            <div className="flex rounded-lg bg-dark-blue p-1">
                {timeRanges.map(range => (
                    <button
                        key={range}
                        onClick={() => onRangeChange(range)}
                        className={`px-3 py-1 rounded-md text-sm font-semibold transition-colors ${selectedRange === range ? 'bg-steel-blue text-off-white' : 'text-light-blue hover:bg-steel-blue/50'}`}
                    >
                        {range}
                    </button>
                ))}
            </div>
            <div className="flex rounded-lg bg-dark-blue p-1">
              <button
                onClick={() => setCurrency(Currency.USD)}
                className={`px-3 py-1 rounded-md text-sm font-semibold transition-colors ${currency === Currency.USD ? 'bg-btc-orange text-dark-blue' : 'text-light-blue hover:bg-steel-blue/50'}`}
              >
                USD
              </button>
              <button
                onClick={() => setCurrency(Currency.EUR)}
                className={`px-3 py-1 rounded-md text-sm font-semibold transition-colors ${currency === Currency.EUR ? 'bg-btc-orange text-dark-blue' : 'text-light-blue hover:bg-steel-blue/50'}`}
              >
                EUR
              </button>
            </div>
        </div>
      </div>
      {renderChart()}
    </div>
  );
};

export default PriceChart;